import {ParamComponent} from 'panel';

import {excludeDemos} from 'common/report/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import type {ItemClickEvent, NoExtension, Section} from 'common/components/items-menu/types';
import type {IronContextRegistry} from 'common/report/context-registry';
import type {CurrentProject, Project} from 'common/report/queries/app-api/project-metadata-client';
import type {Organization} from 'common/report/queries/app-api/user-metadata-client';
import {BoardMoveSetting} from 'common/report/queries/app-api/project-metadata-client';
import {Placement} from 'common/components/tooltip/types';
// If nested_dashboard ff is not available, we don't display the boards selector
import {Project as UserDetailsProject, userDetailsStore} from 'common/report/stores/user-details';
import {isNestedDashboardsEnabled} from 'common/report/util/dashboards/utils';
import {ProjectFeatureFlag} from 'common/report/constants';
import {trackHotJarEvent} from 'common/util/hotjar';

import {template} from './template';
import css from './index.inline.styl';
import {track, TrackingEvent} from './tracking';

enum SortingMethod {
  ALPHA_WITH_CURRENT_FIRST = 1,
}

interface SortableEntity {
  name: string;
  id: number;
}

type EntitySortingFunction = (a: SortableEntity, b: SortableEntity) => number;
const SORTING_FUNCTION_DISPATCH_TABLE: Map<SortingMethod, (id: number) => EntitySortingFunction> = new Map([
  [
    SortingMethod.ALPHA_WITH_CURRENT_FIRST,
    (id) => {
      return (a, b) => {
        if (a.id === id) {
          return -1;
        } else if (b.id === id) {
          return 1;
        }
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      };
    },
  ],
]);

/**
 * This type indicates a Map where the keys (organizations) are in sorted order, so that map.keys() produces the
 * correct ordering, and each value (a list of projects) is correctly ordered as well. What "correct" is depends
 * on which SortingMethod is used to create the SortedOrgMap.
 */
type SortedOrgMap = Map<Organization, UserDetailsProject[]>;

// For local code clarity only; do not export.
type ProjectId = number;

type TargetProjectSelectorMenuParams = {
  projectSearchFilter: string;
  isFeatureGateEnterpriseTier: boolean;
  fillWidth: boolean;
};

export type ProjectSelectedEvent = CustomEvent<{projectId: number; isNestedDashboardsEnabled: boolean}>;

export class TargetProjectSelectorMenu extends ParamComponent<
  TargetProjectSelectorMenuParams,
  EmptyObj,
  IronContextRegistry
> {
  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`currentProject` as const, `currentUser` as const, `projects` as const],
      defaultState: {},
      helpers: {
        fireProjectSelectedEvent: (ev: ItemClickEvent): void => {
          track(TrackingEvent.MoveToYourBoardsProjectSelected);
          trackHotJarEvent(TrackingEvent.MoveToYourBoardsProjectSelected);
          const projectId = ev.detail.item.value;
          const project = this.getProjects().find((p) => p.id === projectId) as any;
          // hack to make this project compatible with CurentProject
          project.hasFeatureFlag = (project) => {
            return project.feature_flags && ProjectFeatureFlag.NestedDashboards in project.feature_flags;
          };
          const _isNestedDashboardsEnabled = isNestedDashboardsEnabled(project);
          this.dispatchEvent(
            new CustomEvent(`projectSelected`, {
              detail: {
                projectId: ev.detail.item.value,
                isNestedDashboardsEnabled: _isNestedDashboardsEnabled,
              },
            }),
          );
        },
        projectListSections: (): Section[] => this.buildProjectListSections(SortingMethod.ALPHA_WITH_CURRENT_FIRST),
      },
      params: {
        projectSearchFilter: String,
        isFeatureGateEnterpriseTier: Boolean,
        fillWidth: Boolean,
      },
      defaultParams: {
        projectSearchFilter: null,
        isFeatureGateEnterpriseTier: false,
      },
    };
  }

  /**
   * The status of the given project's move-between-boards setting.
   */
  private getMoveBetweenBoardsSetting(currentProject: CurrentProject): BoardMoveSetting {
    return currentProject.crossProjectBoardMovingSetting ?? BoardMoveSetting.ALLOWED_WITHIN_ORG;
  }

  /**
   * Gets a list of all the organizations the user can write to.
   */
  private getUserOrganizations(): Organization[] {
    const allUserOrgs = Object.values(this.getContext(`currentUser`).get().value?.organizations || {});
    return excludeDemos(allUserOrgs);
  }

  /**
   * Given the list of organizations, creates a mapping between each organization and the projects from
   * the given list of projects that belong to it.
   */
  private bucketProjectsByOrg(
    orgs: Organization[],
    projects: UserDetailsProject[],
  ): Map<Organization, UserDetailsProject[]> {
    const buckets: Map<Organization, UserDetailsProject[]> = new Map();
    orgs.forEach((org) =>
      buckets.set(
        org,
        projects.filter((proj) => (proj as any).organizationId === org.id),
      ),
    );
    return buckets;
  }

  /**
   * Sorts the given orgToProjectsMap using the given methodology. The result is a map whose keys are in sorted order and whose
   * values are arrays that are also each in sorted order.
   */
  private sortMenu(
    orgToProjectsMap: Map<Organization, UserDetailsProject[]>,
    methodology: SortingMethod,
    currentProject: Project,
  ): SortedOrgMap {
    const sortingFunctionBuilder = SORTING_FUNCTION_DISPATCH_TABLE.get(methodology);
    const organizationSortingFunction = sortingFunctionBuilder
      ? sortingFunctionBuilder(currentProject.organizationId)
      : undefined;
    const sortedOrganizations = [...orgToProjectsMap.keys()].sort(organizationSortingFunction);

    const sortedOrgMap: SortedOrgMap = new Map();
    const projectSortingFunction = sortingFunctionBuilder ? sortingFunctionBuilder(currentProject.id) : undefined;
    for (const organization of sortedOrganizations) {
      const sortedProjects = orgToProjectsMap.get(organization)?.sort(projectSortingFunction) || [];
      sortedOrgMap.set(organization, sortedProjects);
    }
    return sortedOrgMap;
  }

  private convertToSectionConfiguration(
    orgToProjectsMap: SortedOrgMap,
    currentProject: CurrentProject,
  ): Array<Section<ProjectId, NoExtension>> {
    const setting = this.getMoveBetweenBoardsSetting(currentProject);
    if (setting === BoardMoveSetting.NOT_ALLOWED) {
      return [];
    }

    const sections = [];
    for (const org of orgToProjectsMap.keys()) {
      if (setting === BoardMoveSetting.ALLOWED_WITHIN_ORG && org.id !== currentProject.organizationId) {
        continue;
      }

      if ((orgToProjectsMap.get(org) || []).length === 0) {
        continue;
      }

      sections.push({
        label: orgToProjectsMap.size === 1 || setting === BoardMoveSetting.ALLOWED_WITHIN_ORG ? `` : org.name,
        items: (orgToProjectsMap.get(org) || []).map((project) => {
          return {
            label: project.name,
            sideLabel: project.id === currentProject.id ? `This Project` : undefined,
            value: project.id,
            ...(!this.params.isFeatureGateEnterpriseTier &&
              project.id === currentProject.id && {
                isDisabled: true,
                tooltip: {
                  text: `Nesting boards within a project is available in the Enterprise Plan. [See Pricing Page](/pricing/)`,
                  'max-width': `180px`,
                  placement: Placement.Left,
                },
              }),
          };
        }),
      });
    }
    return sections;
  }

  private getProjects(): UserDetailsProject[] {
    // TODO nested-boards: use this line instead after nested boards is at 100%
    // return this.getContext(`projects`).getAllValues();

    const {projects} = userDetailsStore.value || {};
    return Object.values(projects).map((project) => ({...project, organizationId: project.organization_id}));
  }

  /**
   * Constructs the organization/project items configuration to display in the drop down. The items are ordered by
   * the given ordering method.
   */
  private buildProjectListSections(sortingMethodology: SortingMethod): Array<Section<ProjectId, NoExtension>> {
    const currentProject = this.getContext(`currentProject`).get().value;
    const writeableOrgs = this.getUserOrganizations();
    const projects = excludeDemos(this.getProjects());
    if (!currentProject || writeableOrgs.length === 0 || projects.length === 0) {
      return [];
    }

    const orgToProjectsMap = this.bucketProjectsByOrg(writeableOrgs, projects);
    const sortedOrgMap = this.sortMenu(orgToProjectsMap, sortingMethodology, currentProject);
    const sections = this.convertToSectionConfiguration(sortedOrgMap, currentProject);
    return sections;
  }

  connectedCallback() {
    super.connectedCallback();
    userDetailsStore.fetch();
  }
}

export const TargetProjectSelectorMenuEl = defineAndMakeJsxFactory(
  `mp-target-project-selector-menu`,
  TargetProjectSelectorMenu,
);
export const TargetProjectSelectorMenuReact = panel2react(`mp-target-project-selector-menu`, TargetProjectSelectorMenu);
